/**
 * 「从备份恢复」的**纯逻辑层** —— 草稿 → 请求的映射 · 零 DOM · 零网络 · 可直测
 *
 * 依据：N 域 PRD **§5.4.2**（恢复）· **§7.4 `AC-N4.16`–`AC-N4.22`** ·
 * `contract/restore.ts` 文件头（**三项计数的口径** + `conflicted ⊆ overwritten`）·
 * 原型 `08-家庭与设置_原型.html` 的「态展示 · 恢复冲突裁决」区。
 *
 * ## 🔴 本文件的三条纪律
 *
 * | # | 纪律 | 怎么做的 |
 * |---|---|---|
 * | ① | 🔴 **端侧零口径** | 三项计数、丢弃清单、`requires` 前提取值**全部照抄服务端**；本文件**不算任何数**（不 `reduce`、不比对、不推导"该不该二次确认"） |
 * | ② | 🔴 **「要不要二次确认」只读 `requires`** | `AC-N4.19` / `AC-N4.20` / `N-D2` 三条闸的判据**在服务端**（`RestorePreviewView.requires`）。端侧若自己写 `mode === 'overwrite'`，就是把同一规则抄了第二遍 —— 服务端将来收紧（如"merge 也可能缺关键表"）时端侧**静默不跟** |
 * | ③ | 🔴 **未裁决的冲突按 `skip`**（不是 `keepLocal`） | 契约原文：`skip` = "还没决定"、**不进**丢弃清单；`keepLocal` = 明确丢弃。默认值搞反 ⇒ 用户没碰的冲突项**全被当成"已决定丢弃"** ⇒ `N-O10` 的丢弃清单会**谎报** |
 *
 * ## 🔴 为什么文件读取与校验也在这里（而不是在组件里）
 *
 * `FileReader` / `JSON.parse` 的**失败形态**（非 JSON / 空文件 / JSON 但不是备份）是
 * **纯函数级**的判断，与 React 无关 ⇒ 放在纯逻辑层，spec 能**不渲染、不出网**直测。
 * 组件只负责"拿到 `File` 对象 ⇒ 调本函数 ⇒ 按返回值分支渲染"。
 *
 * ⚠️ **形状校验刻意只做"像不像备份"这一层**：真正的表/列/主键校验在服务端
 * （`restore-schema.ts` 从 `PRAGMA` 现取）。端侧若在这里写一遍表清单 = 第二份 schema 登记
 * （`DR-021` 禁令的同型问题）⇒ 这里**只认 `format` 与 `tables` 两个构造性字段**。
 */

import {
  RESTORE_MODES,
  RESTORE_RESOLUTIONS,
  type RestoreDecision,
  type RestoreMode,
  type RestorePreviewView,
  type RestoreRequest,
  type RestoreResolution,
} from '@jiaying/shared';

// ─────────────────────────── 文件解析 ───────────────────────────

/** 读取结果（判别联合 —— 让组件**无法**忘记处理失败态） */
export type BackupReadResult =
  | { readonly ok: true; readonly backup: unknown }
  | { readonly ok: false; readonly reason: string };

/** 备份文件在端侧能认出的**最小形状**（构造性字段由 `toBackupJson` 注入 ⇒ 不可能失真） */
interface BackupShape {
  readonly format?: unknown;
  readonly tables?: unknown;
}

/**
 * 把 `.json` 文件文本解析成"待上传的备份对象"。
 *
 * 🔴 **只做端侧能负责的那一层**：它是不是一份**看起来像备份**的 JSON。
 * "这份备份能不能恢复到本账本"（版本 / 缺表 / 未知列）**一律由服务端判定并如实报错** ——
 * 端侧提前拦下来，用户就看不到服务端那条**带表名**的具体诊断。
 *
 * ⚠️ `format` 的期望值**不在这里写死**：只检查它**存在**（字符串）。为什么？
 * 备份格式版本会演进（v1 的文件将来也可能要能读 —— 那是服务端 `format` 分派的事），
 * 端侧写死当前值 ⇒ 老备份在端侧就被拒，连服务端的诊断都到不了用户眼前。
 */
export function readBackupText(text: string): BackupReadResult {
  if (text.trim() === '') return { ok: false, reason: '文件是空的' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, reason: '不是有效的 JSON 文件（可能选错了文件，或文件已损坏）' };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, reason: '备份文件的顶层应该是一个对象' };
  }
  const shape = parsed as BackupShape;
  if (typeof shape.format !== 'string' || shape.format === '') {
    return { ok: false, reason: '缺少 `format` 字段 —— 这看起来不是家盈导出的备份文件' };
  }
  if (!Array.isArray(shape.tables)) {
    return { ok: false, reason: '缺少 `tables` 字段 —— 这看起来不是家盈导出的备份文件' };
  }
  return { ok: true, backup: parsed };
}

// ─────────────────────────── 裁决草稿 ───────────────────────────

/** 冲突项的**稳定键**（`表|主键` —— 与 `RestoreDecision` 的两字段一致） */
export function decisionKeyOf(table: string, key: string): string {
  return `${table}|${key}`;
}

/** 裁决草稿 = 冲突项稳定键 → 裁决（**只放用户明确选过的**） */
export type DecisionsDraft = Readonly<Record<string, RestoreResolution>>;

/**
 * 读某一项当前的裁决 —— 🔴 **未出现的项 = `skip`**（不是 `keepLocal`）。
 *
 * 见文件头纪律 ③：默认成 `keepLocal` 会让"没决定的项"直接进丢弃清单。
 */
export function resolutionOf(draft: DecisionsDraft, table: string, key: string): RestoreResolution {
  return draft[decisionKeyOf(table, key)] ?? 'skip';
}

/** 写入一项裁决（返回新对象 —— 组件里直接 `setDraft`） */
export function withResolution(
  draft: DecisionsDraft,
  table: string,
  key: string,
  resolution: RestoreResolution,
): DecisionsDraft {
  return { ...draft, [decisionKeyOf(table, key)]: resolution };
}

/** 批量裁决（原型的「全部保留本地 / 全部采用备份」—— 对**本次预览列出的**冲突项生效） */
export function withAllResolutions(
  draft: DecisionsDraft,
  conflicts: readonly { readonly table: string; readonly key: string }[],
  resolution: RestoreResolution,
): DecisionsDraft {
  let next = draft;
  for (const c of conflicts) next = withResolution(next, c.table, c.key, resolution);
  return next;
}

/** 已裁决项数（UI 提示"还有 N 笔没决定"—— **算出来**，不写死） */
export function decidedCount(preview: RestorePreviewView, draft: DecisionsDraft): number {
  let n = 0;
  for (const c of preview.conflicts) {
    if (draft[decisionKeyOf(c.table, c.key)] !== undefined) n += 1;
  }
  return n;
}

// ─────────────────────────── 请求装配 ───────────────────────────

/**
 * 前端**必须**满足的前置条件（逐条对应服务端 `requires` 的字段）。
 *
 * 🔴 `ackMissingTables` **只有服务端要求时才需要**（`requires.missingTablesAck`）——
 * 但契约里它是个可选布尔 ⇒ 若服务端**没要求**而端侧凭"备份里看起来缺表"自己勾上，
 * 那就是端侧在做版本判定（`DR-021` 禁令）。⇒ 一律以 `requires` 为准。
 */
export interface RestorePrerequisites {
  /** 已导出备份（`N-O5`） */
  readonly exportedBeforehand: boolean;
  /** 二次输入的账本名（`AC-N4.20`） */
  readonly confirmLedgerName: string;
  /** 缺关键表的显式确认（`N-D2`） */
  readonly acknowledgeMissingTables: boolean;
}

/** 草稿的完整状态（UI 持它，本函数负责映射） */
export interface RestoreDraft {
  readonly backup: unknown;
  readonly mode: RestoreMode;
  readonly draft: DecisionsDraft;
  readonly prereq: RestorePrerequisites;
}

/** 某条前置条件**是否被满足**（🔴 判据来自 `requires` 与用户输入，不来自模式推断） */
export function isPrerequisiteSatisfied(
  preview: RestorePreviewView,
  draft: RestoreDraft,
): boolean {
  const { requires } = preview;
  if (requires.preExport && !draft.prereq.exportedBeforehand) return false;
  if (requires.confirmLedgerName && draft.prereq.confirmLedgerName !== preview.backupLedgerName) {
    return false;
  }
  if (requires.missingTablesAck && !draft.prereq.acknowledgeMissingTables) return false;
  return true;
}

/**
 * 草稿 → `POST /api/settings/restore` 的请求体。
 *
 * ## 🔴 三条装配规则
 *
 * 1. **`decisions` 只发用户明确选过的项** —— 未出现的项服务端按 `skip` 处理（契约原文），
 *    这正是我们要的语义；把 `skip` 也显式发一遍**不会更正确**，只会把请求体撑大。
 * 2. **`exportedBeforehand` / `acknowledgeMissingTables` 按 `requires` 决定发不发** ——
 *    服务端**没要求**时，端侧**不发这个键**（用展开合并剔键：`v: undefined` 的键**仍在**，
 *    见 `㊸` 批铁律 44）。理由：服务端将来若把这两条改成"发了就要查证据"，
 *    端侧无脑发 `true` 会立刻炸。
 * 3. **`confirmLedgerName` 只在需要时发**（同上），且发的是**用户输入的原值** ——
 *    比对在服务端（`AC-N4.20`）。端侧**不拦**（拦了就是一个永远调不通的死按钮：
 *    用户输入错的账本名应该看到服务端那句"账本名不一致"，而不是按钮点不动）。
 */
export function buildRestoreRequest(preview: RestorePreviewView, draft: RestoreDraft): RestoreRequest {
  const decisions: RestoreDecision[] = [];
  for (const c of preview.conflicts) {
    const resolution = draft.draft[decisionKeyOf(c.table, c.key)];
    if (resolution !== undefined) decisions.push({ table: c.table, key: c.key, resolution });
  }

  // 🔴 展开合并**剔键**（不是 `v: undefined`）—— 见规则 2
  const optional: {
    exportedBeforehand?: boolean;
    acknowledgeMissingTables?: boolean;
    confirmLedgerName?: string;
  } = {};
  if (preview.requires.preExport) optional.exportedBeforehand = draft.prereq.exportedBeforehand;
  if (preview.requires.confirmLedgerName) optional.confirmLedgerName = draft.prereq.confirmLedgerName;
  if (preview.requires.missingTablesAck) {
    optional.acknowledgeMissingTables = draft.prereq.acknowledgeMissingTables;
  }

  return {
    backup: draft.backup,
    mode: draft.mode,
    ...(decisions.length === 0 ? {} : { decisions }),
    ...optional,
  };
}

// ─────────────────────────── 文案派生 ───────────────────────────

// ⚠️ 入口标题 / 按钮文案**不在这里** —— 它们是设置页「数据主权区」的文案，
//    唯一来源 = `features/sovereignty-labels.ts`（`RESTORE_TITLE` / `RESTORE_ACTION`）。
//    本文件只放**恢复流程内部**派生出来的文案（与 `preview` / `result` 绑定）。

/**
 * 冲突项一行的事实描述（🔴 **只陈述事实，不给建议**）。
 *
 * 原型给的两条样例正是这个形态：「本地「日用百货」· 备份「食品酒饮」—— 分类不一致」、
 * 「本地已软删除 · 备份中存在 —— 删除优先还是恢复」。⇒ 本函数产出**前半句事实**，
 * 后半句的"该怎么选"**留给用户**（产品不在 UI 里替用户判断 —— 那会变成第二份口径）。
 *
 * 🔴 **不给整行、只给变化列**（契约层刻意如此：整行下发会把响应撑到与备份同量级）。
 */
export function conflictFactText(
  changedColumns: readonly string[],
  localValues: Readonly<Record<string, string | number | null>>,
  backupValues: Readonly<Record<string, string | number | null>>,
): string {
  const parts = changedColumns.map((col) => {
    return `${col}：本地 ${String(localValues[col] ?? '（空）')} · 备份 ${String(backupValues[col] ?? '（空）')}`;
  });
  return parts.join('；');
}

/** 冲突条数提示（🔴 截断时必须明示"另有 N 笔"—— 静默截断 = 让用户以为就这么多） */
export function conflictCountText(preview: RestorePreviewView): string {
  const total = preview.counts.conflicted;
  const shown = preview.conflicts.length;
  if (!preview.conflictsTruncated) return `共 ${String(total)} 笔，已全部列出`;
  return `共 ${String(total)} 笔，此处列出前 ${String(shown)} 笔，另有 ${String(total - shown)} 笔按「跳过」处理`;
}

/** 覆盖模式的冲击提示（"覆盖 96 笔"容易被读成"只改 96 行"—— 这里说清是清空重写） */
export function overwriteImpactText(preview: RestorePreviewView): string {
  return `覆盖模式会先清空本账本的 ${String(preview.overwriteImpact.tables)} 张表、共 ${String(preview.overwriteImpact.rows)} 行，再把备份整份写回。`;
}

/** 写入结果一行（`inserted` 新插入 / `updated` 改写既有行） */
export function writtenText(result: { inserted: number; updated: number }): string {
  return `新插入 ${String(result.inserted)} 行 · 改写 ${String(result.updated)} 行`;
}

/**
 * 完整性核验一行 —— 🔴 **三项都为 0 才说"通过"**，否则**如实报数**。
 *
 * `AC-N4.22` 的运行期半边（真恒等式在服务端集成测试里）：这三项非 0 意味着
 * **已经静默丢了数据**（腿指向不存在的账户 = 那笔钱永远不进余额）⇒ 必须显眼上报。
 */
export function integrityText(i: {
  legsWithoutAccount: number;
  transactionsWithoutLegs: number;
  crossLedgerLegs: number;
}): { readonly ok: boolean; readonly text: string } {
  const total = i.legsWithoutAccount + i.transactionsWithoutLegs + i.crossLedgerLegs;
  if (total === 0) return { ok: true, text: '引用完整性检查通过（三项均为 0）' };
  return {
    ok: false,
    text: `⚠️ 引用完整性异常：无账户的腿 ${String(i.legsWithoutAccount)} · 无腿的交易 ${String(i.transactionsWithoutLegs)} · 跨账本的腿 ${String(i.crossLedgerLegs)}`,
  };
}

/** 备份来源一行（原型右侧那句「来源 8/31 备份 · 3.2 MB」的**日期半边**；体积端侧拿不到） */
export function backupOriginText(preview: RestorePreviewView): string {
  const d = new Date(preview.backupExportedAt);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `来源 ${String(d.getFullYear())}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} 的备份 · 账本「${preview.backupLedgerName}」`;
}

// ─────────────────── 守卫：枚举与契约**同源**（不写第二份） ───────────────────

/** 🔴 模式选项**由契约枚举派生**（自己列 `['merge','overwrite']` = 第二份口径） */
export function modeOptions(): readonly RestoreMode[] {
  return RESTORE_MODES;
}

/** 🔴 裁决选项同上 —— 原型三个按钮的顺序 = 契约枚举顺序（`keepLocal` / `useBackup` / `skip`） */
export function resolutionOptions(): readonly RestoreResolution[] {
  return RESTORE_RESOLUTIONS;
}
